import { Button, Card, Select, Spin, Typography } from "antd";
import { Chat } from "enums/ChatType";
import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import EmptyIllustrationSrc from "assets/images/messenger-empty.png";
import "./Messenger.scss";
import { useDispatch, useSelector } from "react-redux";
import {
  addNewChat,
  addNewMessage,
  deleteMessage,
  getAllChats,
  loadMoreChats,
  setUnreadCountForChat,
} from "redux-context/chat";
import { sseService } from "services/SSEStream";
import { default as ChatDetails } from "./Chat";
import { isMobile } from "react-device-detect";
import { CollabChatItem } from "components/messenger/CollabChatItem";
import { MustardChaItem } from "components/messenger/MustardChaItem";
import { BusinessMustardChatItem } from "components/messenger/BusinessMustardChatItem";
import { UserState } from "redux-context/user";
import { DomainState } from "redux-context/domain";

type ChatType = "all" | "business" | "influencer" | "unreplied";

const Messenger: React.FC = () => {
  const dispatch = useDispatch<any>();
  const { chats, loading } = useSelector((state: any) => state.chat);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeChatId, setActiveChatId] = useState<string | null>(null);

  const { loggedUser } = useSelector((state: UserState) => state.user);
  const { metadata } = useSelector((state: DomainState) => state.domain);
  const isAdminUser = loggedUser?.email?.endsWith(`@${metadata?.domain}`);

  // Sorting and filtering state
  const [sortBy, setSortBy] = useState<"newest" | "oldest">(
    (searchParams.get("sortBy") as "newest" | "oldest") ?? "newest"
  );
  const [chatType, setChatType] = useState<ChatType>(() => {
    const chatTypeParam = searchParams.get("chatType");
    return (chatTypeParam as ChatType) || "all";
  });

  // Pagination and loading states
  const [pagination, setPagination] = useState<{
    total: number;
    offset: number;
    limit: number;
    hasMore: boolean;
  }>({
    total: 0,
    offset: 0,
    limit: 3,
    hasMore: false,
  });
  const [loadingMore, setLoadingMore] = useState(false);

  const newChatSubscribeRef = useRef<() => void>();
  const newMessageSubscribeRef = useRef<() => void>();
  const unreadCountForChatSubscribeRef = useRef<() => void>();
  const deleteMessageSubscribeRef = useRef<() => void>();

  // No client-side filtering or sorting - all done on the backend
  const displayChats = chats || [];

  useEffect(() => {
    if (displayChats?.length > 0 && !activeChatId && !isMobile) {
      setActiveChatId(displayChats[0]._id);
    }
  }, [displayChats, activeChatId]);

  const handleLoadMore = () => {
    if (!pagination.hasMore || loadingMore) return;
    
    setLoadingMore(true);
    dispatch(loadMoreChats({ sortBy, chatType })).finally(() => {
      setLoadingMore(false);
    });
  };

  useEffect(() => {
    dispatch(getAllChats({ sortBy, chatType })).then((result: any) => {
      if (result.payload?.pagination) {
        setPagination(result.payload.pagination);
      }
    });

    newChatSubscribeRef.current = sseService.subscribe(
      isAdminUser ? "superchat/new-chat" : "chat/new-chat",
      (event) => {
        console.log("chat/new-chat");
        const data = JSON.parse(event.data);
        const chat = data.chat as Chat;
        dispatch(addNewChat(chat));
      }
    );

    newMessageSubscribeRef.current = sseService.subscribe(
      isAdminUser ? "superchat/new-message" : "chat/new-message",
      (event) => {
        console.log("chat/new-message");
        const message = JSON.parse(event.data) as Chat["messages"][0];
        dispatch(addNewMessage(message));
      }
    );

    unreadCountForChatSubscribeRef.current = sseService.subscribe(
      isAdminUser
        ? "superchat/unread-count-for-chat"
        : "chat/unread-count-for-chat",
      (event) => {
        console.log(event.data);
        const { chatId, unreadCount } = JSON.parse(event.data) as {
          chatId: string;
          unreadCount: number;
        };
        console.log("chat/unread-count-for-chat", chatId, unreadCount);
        dispatch(setUnreadCountForChat({ chatId, unreadCount }));
      }
    );

    deleteMessageSubscribeRef.current = sseService.subscribe(
      isAdminUser ? "superchat/delete-message" : "chat/delete-message",
      (event) => {
        const data = JSON.parse(event.data) as {
          chatId: string;
          messageId: string;
        };
        dispatch(
          deleteMessage({ chatId: data.chatId, messageId: data.messageId })
        );
      }
    );

    return () => {
      newChatSubscribeRef.current?.();
      newMessageSubscribeRef.current?.();
      unreadCountForChatSubscribeRef.current?.();
      deleteMessageSubscribeRef.current?.();
    };
  }, [dispatch, isAdminUser]);

  if (loading) {
    return (
      <div>
        <Card className="messenger__card">
          <Spin />
        </Card>
      </div>
    );
  }

  return (
    <div className={`messenger ${isMobile ? "--mobile" : ""}`}>
      <Card styles={{ body: { padding: 0 } }} className="messenger__container">
        {!loading && displayChats?.length === 0 ? (
          <div className="messenger__empty">
            <div className="messenger__empty-container">
              <img src={EmptyIllustrationSrc} alt="empty" />
              <Typography.Title level={3}>No chats yet</Typography.Title>
              <Typography.Text>Your chat history is empty</Typography.Text>
            </div>
          </div>
        ) : (
          <div className="messenger__chat-container">
            <div className="messenger__chat-grid">
            <div>
                <Select
                  className="supermessenger-messenger__select"
                  value={sortBy}
                  onChange={(value) => {
                    setSearchParams({
                      ...Object.fromEntries(searchParams.entries()),
                      sortBy: value,
                    });
                    setSortBy(value);
                    
                    // Fetch new data with new sorting
                    dispatch(getAllChats({ sortBy: value, chatType })).then((result: any) => {
                      if (result.payload?.pagination) {
                        setPagination(result.payload.pagination);
                      }
                    });
                  }}
                  options={[
                    {
                      value: "newest",
                      label: (
                        <span className="supermessenger-messenger__select-option">
                          Newest
                        </span>
                      ),
                    },
                    {
                      value: "oldest",
                      label: (
                        <span className="supermessenger-messenger__select-option">
                          Oldest
                        </span>
                      ),
                    },
                  ]}
                />
              </div>
              <div className="supermessenger-messenger__chat-type-filter">
                {[
                  { label: "All chats", value: "all" as ChatType },
                  { label: "Unreplied", value: "unreplied" as ChatType },
                ].map((option) => {
                  const isChecked = chatType === option.value;
                  return (
                    <Button
                      key={option.value}
                      type={isChecked ? "primary" : "default"}
                      className="supermessenger-messenger__chat-type-button"
                      onClick={() => {
                        if (isChecked) {
                          // Don't allow unchecking if it's already selected
                          return;
                        }
                        
                        setChatType(option.value);
                        setSearchParams({
                          ...Object.fromEntries(searchParams.entries()),
                          chatType: option.value,
                        });
                        
                        // Fetch new data with new filter
                        dispatch(getAllChats({ sortBy, chatType: option.value })).then((result: any) => {
                          if (result.payload?.pagination) {
                            setPagination(result.payload.pagination);
                          }
                        });
                      }}
                    >
                      {option.label}
                    </Button>
                  );
                })}
              </div>
              {displayChats?.map((chat: Chat, index: number) => (
                <React.Fragment key={chat._id}>
                  {chat.context.type === "collab" && (
                    <CollabChatItem
                      key={chat._id}
                      chat={chat}
                      onClick={() => {
                        if (isMobile) {
                          navigate(`/messenger/${chat._id}`);
                        } else {
                          setActiveChatId(chat._id);
                        }
                      }}
                      isActive={chat._id === activeChatId}
                    />
                  )}
                  {chat.context.type === "foodie-mustard" && (
                    <MustardChaItem
                      key={chat._id}
                      chat={chat}
                      onClick={() => {
                        if (isMobile) {
                          navigate(`/messenger/${chat._id}`);
                        } else {
                          setActiveChatId(chat._id);
                        }
                      }}
                      showSupportBadge={false}
                      isActive={chat._id === activeChatId}
                    />
                  )}
                  {chat.context.type === "business-mustard" && (
                    <BusinessMustardChatItem
                      key={chat._id}
                      chat={chat}
                      onClick={() => {
                        if (isMobile) {
                          navigate(`/messenger/${chat._id}`);
                        } else {
                          setActiveChatId(chat._id);
                        }
                      }}
                      isActive={chat._id === activeChatId}
                      view="business"
                    />
                  )}
                </React.Fragment>
              ))}
              {pagination.hasMore && !loadingMore && (
                <div className="messenger__load-more">
                  <Button
                    onClick={handleLoadMore}
                    loading={loadingMore}
                    type="link"
                  >
                    Load More
                  </Button>
                </div>
              )}
              {loadingMore && (
                <div className="messenger__load-more">
                  <Spin />
                </div>
              )}
            </div>
            {!isMobile && (
              <div className="messenger__chat-details">
                {activeChatId && <ChatDetails id={activeChatId} />}
              </div>
            )}
          </div>
        )}
      </Card>
      <div className="disclaimer">
        We actively monitor all chats, but if you need immediate support, please
        tag @mustard to request our intervention, and we’ll assist you right
        away.
      </div>
    </div>
  );
};

export default Messenger;
